import React, { useEffect, useState } from 'react';
import useConfig from 'hooks/useConfig';
import { Capacitor } from '@capacitor/core';
import Overlay from 'components/Overlay';
import usePersistedState from 'hooks/usePersistedState';
import { Button } from '@mui/material';
import InstallMobileRoundedIcon from '@mui/icons-material/InstallMobileRounded';
import { isApp } from 'utils/app';
import { App } from '@capacitor/app';
import { captureException } from 'utils/error';

function AppUpdateOverlay() {
  const capacitorConfig = useConfig('pwa.capacitor');
  const { googlePlayLink, appStoreLink } = useConfig('web.downloadApp', {});
  const [isClosed, setIsClosed] = usePersistedState(false, {
    key: 'update-overlay/is-closed',
    ttl: 1000 * 60 * 60 * 24 * 3,
  });
  const [{ regularUpdate, forceUpdate }, setState] = useState({ regularUpdate: false, forceUpdate: false });

  useEffect(() => {
    if (!isApp()) {
      return;
    }
    checkVersion();

    async function checkVersion() {
      const platform = Capacitor.getPlatform();
      let currentVersion = null;
      try {
        currentVersion = (await App.getInfo()).version;
      } catch (e) {
        captureException(e);
        return;
      }

      if (platform === 'android' && googlePlayLink) {
        checkUpdateNeeded({
          latestVersion: capacitorConfig.android?.latestVersion ?? capacitorConfig.latestVersion,
          latestSupportedVersion: capacitorConfig.android?.latestSupportedVersion ?? capacitorConfig.latestSupportedVersion,
        });
      } else if (platform === 'ios' && appStoreLink) {
        checkUpdateNeeded({
          latestVersion: capacitorConfig.ios?.latestVersion ?? capacitorConfig.latestVersion,
          latestSupportedVersion: capacitorConfig.ios?.latestSupportedVersion ?? capacitorConfig.latestSupportedVersion,
        });
      }
      function checkUpdateNeeded({ latestVersion, latestSupportedVersion }) {
        if (isOlder(latestSupportedVersion)) {
          setState({ forceUpdate: true });
        } else if (isOlder(latestVersion)) {
          setState({ regularUpdate: true });
        } else {
          setState({ regularUpdate: false, forceUpdate: false });
        }
      }

      function isOlder(target) {
        if (!currentVersion || !target) {
          return false;
        }
        return currentVersion.localeCompare(target, undefined, { numeric: true, sensitivity: 'base' }) < 0;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacitorConfig]);

  function getDownloadUrl() {
    if (Capacitor.getPlatform() === 'android') {
      return googlePlayLink;
    }
    return appStoreLink;
  }

  return (
    <Overlay
      open={(regularUpdate && !isClosed) || forceUpdate}
      onClose={forceUpdate ? undefined : () => setIsClosed(true)}
    >
      <Overlay.Header>
        <InstallMobileRoundedIcon />
        Update Available
      </Overlay.Header>
      <Overlay.Content>
        {
          forceUpdate ? 'A new update is available, and this version is not supported anymore. please download the latest version.' : 'A new update is available!'
        }
      </Overlay.Content>
      <Overlay.Actions>
        {
          !forceUpdate && <Button fullWidth onClick={() => setIsClosed(true)}>Later</Button>
        }
        <Button variant="contained" fullWidth href={getDownloadUrl()} rel="noopener noreferrer" target="_blank">Install</Button>
      </Overlay.Actions>
    </Overlay>
  );
}

export default AppUpdateOverlay;
